/**
 * VoiceRecorder — Browser microphone recording for Document Studio
 *
 * State machine:
 *   IDLE → REQUESTING_PERMISSION → PERMISSION_DENIED
 *                                → RECORDING (MediaRecorder, animated waveform)
 *                                  → REVIEWING (audio playback, duration)
 *                                    → [Discard] → IDLE
 *                                    → [Use Recording] → UPLOADING → DONE | UPLOAD_ERROR
 *
 * Format: audio/webm;codecs=opus (Chrome/Firefox) | audio/mp4 (Safari)
 * The audio blob is uploaded via useCaptureUpload → presigned S3 → chaos pipeline.
 * Backend chaos AI will detect the audio MIME and trigger Bedrock Whisper transcription.
 *
 * DRR-128: only the recording blob is written; no raw stream is persisted to DDB.
 * DRR-114: audio files are T4 data — backend applies 30-day TTL at write time.
 * Mantine v8.x only. No external waveform library.
 */

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Title,
  ThemeIcon,
  Button,
  Alert,
  Progress,
  Badge,
  Box,
  Center,
} from '@mantine/core';
import {
  IconMicrophone,
  IconPlayerStop,
  IconPlayerPlay,
  IconTrash,
  IconCheck,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useCaptureUpload } from './hooks/useCaptureUpload';
import type { UploadResult } from './hooks/useChaosDocumentUpload';

// ============================================================
// Types
// ============================================================

type VoicePhase =
  | 'IDLE'
  | 'REQUESTING_PERMISSION'
  | 'PERMISSION_DENIED'
  | 'RECORDING'
  | 'REVIEWING'
  | 'UPLOADING'
  | 'UPLOAD_ERROR'
  | 'DONE';

interface VoiceState {
  phase: VoicePhase;
  blob?: Blob;
  durationMs?: number;
  objectUrl?: string;
  errorMessage?: string;
}

export interface VoiceRecorderProps {
  onUploadComplete?: (results: UploadResult[]) => void;
  onError?: (error: Error) => void;
  sourceSystem?: string;
}

// ============================================================
// Animated waveform bars (pure CSS, no library)
// ============================================================

const WaveformBars: React.FC<{ isRecording: boolean }> = ({ isRecording }) => {
  const BAR_COUNT = 20;
  return (
    <Group gap={3} justify="center" align="flex-end" style={{ height: 48 }}>
      {Array.from({ length: BAR_COUNT }).map((_, i) => (
        <Box
          key={i}
          style={{
            width: 4,
            borderRadius: 2,
            backgroundColor: isRecording
              ? 'var(--mantine-color-red-6)'
              : 'var(--mantine-color-gray-4)',
            height: isRecording ? undefined : 8,
            animation: isRecording
              ? `waveform-bounce ${0.6 + (i % 5) * 0.08}s ease-in-out infinite alternate`
              : undefined,
            minHeight: 4,
            maxHeight: 44,
          }}
        />
      ))}
      <style>{`
        @keyframes waveform-bounce {
          from { height: 4px; }
          to { height: ${Math.floor(Math.random() * 40) + 4}px; }
        }
      `}</style>
    </Group>
  );
};

// ============================================================
// Helper
// ============================================================

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function getSupportedMimeType(): string {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
}

function mimeToExtension(mime: string): string {
  if (mime.startsWith('audio/webm')) return 'webm';
  if (mime.startsWith('audio/ogg')) return 'ogg';
  if (mime.startsWith('audio/mp4')) return 'm4a';
  return 'audio';
}

// ============================================================
// Component
// ============================================================

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onUploadComplete,
  onError,
  sourceSystem = 'document-studio-voice',
}) => {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [state, setState] = useState<VoiceState>({ phase: 'IDLE' });
  const [elapsedMs, setElapsedMs] = useState(0);

  const { uploadBlob, uploadProgress, error: uploadError } = useCaptureUpload();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (state.objectUrl) URL.revokeObjectURL(state.objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = useCallback(async () => {
    setState({ phase: 'REQUESTING_PERMISSION' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const mimeUsed = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeUsed });
        const objectUrl = URL.createObjectURL(blob);
        const durationMs = Date.now() - startTimeRef.current;
        if (timerRef.current) clearInterval(timerRef.current);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setState({ phase: 'REVIEWING', blob, objectUrl, durationMs });
      };

      recorder.start(250); // collect chunks every 250ms
      startTimeRef.current = Date.now();

      timerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startTimeRef.current);
      }, 100);

      setState({ phase: 'RECORDING' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Microphone access denied';
      setState({ phase: 'PERMISSION_DENIED', errorMessage: msg });
    }
  }, []);

  const stopRecording = useCallback(() => {
    recorderRef.current?.stop();
  }, []);

  const discard = useCallback(() => {
    if (state.objectUrl) URL.revokeObjectURL(state.objectUrl);
    setElapsedMs(0);
    setState({ phase: 'IDLE' });
  }, [state.objectUrl]);

  const useRecording = useCallback(async () => {
    if (!state.blob) return;
    setState((s) => ({ ...s, phase: 'UPLOADING' }));
    try {
      const mimeUsed = state.blob.type || 'audio/webm';
      const ext = mimeToExtension(mimeUsed);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `voice-note-${timestamp}.${ext}`;
      const result = await uploadBlob(state.blob, filename, { sourceSystem });
      if (state.objectUrl) URL.revokeObjectURL(state.objectUrl);
      setElapsedMs(0);
      setState({ phase: 'DONE' });
      onUploadComplete?.([result]);
    } catch (err) {
      const uploadErr = err instanceof Error ? err : new Error('Upload failed');
      setState((s) => ({ ...s, phase: 'UPLOAD_ERROR', errorMessage: uploadErr.message }));
      onError?.(uploadErr);
    }
  }, [state.blob, state.objectUrl, uploadBlob, sourceSystem, onUploadComplete, onError]);

  const reset = useCallback(() => {
    if (state.objectUrl) URL.revokeObjectURL(state.objectUrl);
    setElapsedMs(0);
    setState({ phase: 'IDLE' });
  }, [state.objectUrl]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group gap="xs">
          <ThemeIcon size="lg" variant="light" color="red">
            <IconMicrophone size={20} />
          </ThemeIcon>
          <div>
            <Title order={4}>Voice Recording</Title>
            <Text size="sm" c="dimmed">
              Record a voice note — automatically transcribed and analyzed
            </Text>
          </div>
          {state.phase !== 'IDLE' && state.phase !== 'DONE' && (
            <Badge ml="auto" color={state.phase === 'RECORDING' ? 'red' : 'blue'} variant="light">
              {state.phase.replace(/_/g, ' ')}
            </Badge>
          )}
        </Group>

        {/* IDLE */}
        {state.phase === 'IDLE' && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={60} radius="xl" variant="light" color="gray">
                <IconMicrophone size={30} />
              </ThemeIcon>
              <Text size="lg" fw={500}>Ready to record</Text>
              <Text size="sm" c="dimmed">
                Your browser will request microphone permission
              </Text>
              <Button
                leftSection={<IconMicrophone size={16} />}
                color="red"
                onClick={startRecording}
                mt="xs"
              >
                Start Recording
              </Button>
            </Stack>
          </Center>
        )}

        {/* REQUESTING_PERMISSION */}
        {state.phase === 'REQUESTING_PERMISSION' && (
          <Center py="xl">
            <Text c="dimmed">Requesting microphone access…</Text>
          </Center>
        )}

        {/* PERMISSION_DENIED */}
        {state.phase === 'PERMISSION_DENIED' && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Microphone Access Denied">
            <Stack gap="xs">
              <Text size="sm">{state.errorMessage}</Text>
              <Text size="xs" c="dimmed">
                Allow microphone access in your browser settings, then try again.
              </Text>
              <Button size="xs" variant="light" onClick={reset}>
                Back
              </Button>
            </Stack>
          </Alert>
        )}

        {/* RECORDING */}
        {state.phase === 'RECORDING' && (
          <Stack gap="sm">
            <Box
              style={{
                padding: '1.5rem',
                borderRadius: 8,
                background: 'var(--mantine-color-red-0)',
                border: '2px solid var(--mantine-color-red-3)',
              }}
            >
              <Stack gap="sm" align="center">
                <WaveformBars isRecording={true} />
                <Text size="xl" fw={700} c="red" ff="monospace">
                  {formatDuration(elapsedMs)}
                </Text>
                <Badge color="red" variant="filled" size="lg">
                  ● RECORDING
                </Badge>
              </Stack>
            </Box>
            <Group justify="center">
              <Button
                leftSection={<IconPlayerStop size={16} />}
                color="red"
                onClick={stopRecording}
                size="md"
              >
                Stop
              </Button>
            </Group>
          </Stack>
        )}

        {/* REVIEWING */}
        {state.phase === 'REVIEWING' && state.objectUrl && (
          <Stack gap="sm">
            <Box
              style={{
                padding: '1rem',
                borderRadius: 8,
                background: 'var(--mantine-color-gray-0)',
                border: '1px solid var(--mantine-color-gray-3)',
              }}
            >
              <Stack gap="sm" align="center">
                <WaveformBars isRecording={false} />
                <Text size="sm" c="dimmed">
                  Duration: {state.durationMs ? formatDuration(state.durationMs) : '—'}
                </Text>
                <audio
                  controls
                  src={state.objectUrl}
                  style={{ width: '100%', marginTop: 4 }}
                />
              </Stack>
            </Box>
            <Group justify="center">
              <Button
                leftSection={<IconCheck size={16} />}
                color="green"
                onClick={useRecording}
              >
                Use Recording
              </Button>
              <Button
                leftSection={<IconTrash size={16} />}
                variant="light"
                color="gray"
                onClick={discard}
              >
                Discard
              </Button>
            </Group>
          </Stack>
        )}

        {/* UPLOADING */}
        {state.phase === 'UPLOADING' && (
          <Stack gap="sm">
            <Progress value={uploadProgress} animated size="sm" />
            <Text ta="center" size="sm" c="dimmed">
              Uploading for transcription… {Math.round(uploadProgress)}%
            </Text>
          </Stack>
        )}

        {/* UPLOAD_ERROR */}
        {state.phase === 'UPLOAD_ERROR' && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Upload Failed">
            <Stack gap="xs">
              <Text size="sm">{state.errorMessage ?? uploadError?.message}</Text>
              <Group>
                <Button size="xs" color="blue" onClick={useRecording}>
                  Retry
                </Button>
                <Button size="xs" variant="subtle" onClick={reset}>
                  Start Over
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {/* DONE */}
        {state.phase === 'DONE' && (
          <Alert color="green" icon={<IconCheck size={16} />} title="Recording Uploaded">
            <Group justify="space-between">
              <Text size="sm">Voice note queued for transcription and analysis.</Text>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconMicrophone size={14} />}
                onClick={reset}
              >
                Record Another
              </Button>
            </Group>
          </Alert>
        )}
      </Stack>
    </Card>
  );
};
